import React, { useState } from "react";
import styles from "./Card.module.scss";
import editIcon from "@/assets/icons/edit-light.svg";
import trashIcon from "@/assets/icons/trash.svg";
import checkIcon from "@/assets/icons/cross.svg";
import addIcon from "@/assets/icons/plus.svg";
import DeleteModal from "@/components/DeleteModal/DeleteModal";
import { PublishModal } from "../PublishModal/PublishModal";
import { IProfileStory, Option } from "@/store/models/books.models";
import {
  useDeleteProfileStoryOptionMutation,
  useUpdateProfileStoryOptionMutation,
} from "@/store/rtk-api/books.api";
import { EditOptionModal } from "../EditOptionModal/EditOptionModal";

type Props = {
  type: string;
  option: Option;
  profileStory: IProfileStory[];
};

export const Card = ({ type, option, profileStory }: Props) => {
  const [isOpenEditModal, setIsOpenEditModal] = useState(false);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [isOpenPublishModal, setIsOpenPublishModal] = useState(false);

  const [deleteOption] = useDeleteProfileStoryOptionMutation();
  const [editOption] = useUpdateProfileStoryOptionMutation();

  const handleToggleEditModal = () => {
    setIsOpenEditModal((prev) => !prev);
  };

  const handleToggleDeleteModal = () => {
    setIsOpenDeleteModal((prev) => !prev);
  };

  const handleDelete = async () => {
    try {
      await deleteOption({ optionId: option.uid }).unwrap();
      setIsOpenDeleteModal(false);
    } catch (error) {
      console.error("Failed to delete option:", error);
    }
  };

  const handleEdit = async (formData: FormData, optionId: string) => {
    try {
      await editOption({ optionId, data: formData }).unwrap();
      setIsOpenEditModal(false);
    } catch (error) {
      console.error("Failed to edit option:", error);
    }
  };

  const handlePublish = async () => {
    const formData = new FormData();
    formData.append("isPublished", (!option.isPublished).toString());

    try {
      await editOption({
        optionId: option.uid,
        data: formData,
      }).unwrap();
      setIsOpenPublishModal(false);
    } catch (error) {
      console.error("Failed to update publish status: ", error);
    }
  };

  const renderDescription = (type: string, option: Option) => {
    const story = profileStory.find((story) => story.name === type);
    if (story && ["Hero", "Villain", "Special item"].includes(story.name)) {
      return <p>{option.description}</p>;
    }
    return null;
  };

  return (
    <div className={styles.item}>
      {option.imgUrl && (
        <img
          className={styles.itemImg}
          src={option.imgUrl}
          alt={option.optionName}
        />
      )}
      <h3 className={styles.itemTitle}>{option.optionName}</h3>
      {renderDescription(type, option)}
      <div className={styles.itemBtns}>
        <button
          className={`${option.isPublished ? styles.itemCheckBtnPublished : styles.itemCheckBtn}`}
          onClick={() => setIsOpenPublishModal((prev) => !prev)}
        >
          {option.isPublished ? (
            <img className={styles.itemBtnImg} src={checkIcon} alt="publish" />
          ) : (
            <img className={styles.itemBtnImg} src={addIcon} alt="unpublish" />
          )}
        </button>
        <button
          className={styles.itemEditBtn}
          onClick={() => handleToggleEditModal()}
        >
          <img className={styles.itemBtnImg} src={editIcon} alt="edit" />
        </button>
        <button
          className={styles.itemDeleteBtn}
          onClick={() => handleToggleDeleteModal()}
        >
          <img className={styles.itemBtnImg} src={trashIcon} alt="delete" />
        </button>
      </div>

      {isOpenDeleteModal && (
        <DeleteModal
          isOpen={isOpenDeleteModal}
          setIsOpen={setIsOpenDeleteModal}
          name={option.optionName}
          supportingText="This is an irreversible action"
          onDelete={handleDelete}
        />
      )}

      {isOpenPublishModal && (
        <PublishModal
          isOpen={isOpenPublishModal}
          setIsOpen={setIsOpenPublishModal}
          onCheck={handlePublish}
          isPublish={option.isPublished}
        />
      )}

      {isOpenEditModal && (
        <EditOptionModal
          isOpen={isOpenEditModal}
          setIsOpen={setIsOpenEditModal}
          onEdit={handleEdit}
          option={option}
          type={type}
        />
      )}
    </div>
  );
};

